/**
 * Full control (top level entry) for shopping cart
 * including checkout buttons and action handlers
 * */
import * as React from "react";

import { CartContext } from "./cart-context";

import * as cartRepo from "../cartRepo";
import * as usersettings from "../../DeliveryLocation/user-settings";

import { ShoppingCartWidget } from "./shopping-cart";
import { CheckoutButtons } from "./CheckoutButtons";

import { ApiContext } from "../../Inventory/context-api";
import { locationRequirement } from "../../DeliveryLocation/Search/lib";
import { LocationSearchModal } from "../../DeliveryLocation/Search/LocationSearchModal";

import { CartLoadingUI } from "./CartLoading";
import { safeReloadPage } from "src/lib/common";
import { SimpleDialog } from "src/components/Common/SimpleDialog";

const CheckInventoryForCart = (address: IDeliveryAddress) => {
  return ApiContext.getStoresForCart(address);
};

const EmptyCart: React.FunctionComponent = () => {
  return (
    <section className="empty-cart">
      <p>Your cart is empty.</p>
      <a className="continue-shopping" href="/Cart/ContinueShopping">
        Continue Shopping
      </a>
    </section>
  );
};

interface IFullCartState {
  canCheckout: boolean;
  cart: ShoppingCart | null;
  isLoading: boolean;
  address: IDeliveryAddress;
  inventory: InventoryResult[] | null;
  needsAddress: boolean;
  marketplaceAllowed: boolean;
}

const openLocationSelection = () => {
  // legacy search for hospital
  document.location.href = "/hospitalsearch.aspx?np=shoppingcart";
};

export class ShoppingCartWithInventory extends React.Component<
  Record<string, never>,
  IFullCartState
> {
  constructor(props) {
    super(props);

    const hospitalAddress = usersettings.getDeliveryAddressInfo();

    this.state = {
      canCheckout: false,
      cart: null,
      isLoading: true,
      address: hospitalAddress,
      needsAddress: false,
      inventory: null,
      marketplaceAllowed: false,
    };
  }

  async loadCart(forceInventoryRefresh = false) {
    const currentState: IFullCartState = {
      ...(this.state as IFullCartState),
    }; // get a copy of the current state.

    // load cart
    currentState.cart = await cartRepo.getCart();

    // check inventory for all products
    if (
      currentState.cart &&
      (!currentState.inventory || forceInventoryRefresh)
    ) {
      currentState.inventory = await CheckInventoryForCart(this.state.address);
    }

    if (
      currentState.address.LocationId === 0 &&
      !currentState.address.PostalCode
    ) {
      currentState.needsAddress = true;
    }

    if (currentState.inventory && currentState.cart) {
      const currentCart = currentState.cart;

      const hasAvailableStore = (currentValue: InventoryResult) =>
        currentValue.StoreNumber ? true : false;

      if (currentState.inventory.every(hasAvailableStore)) {
        currentState.canCheckout = true;
      }

      // map the inventory levels with the products
      const inventory = currentState.inventory;
      inventory.forEach((result: InventoryResult) => {
        const prodInventory = result.InventoryInfo;
        for (const key in prodInventory) {
          if (Object.prototype.hasOwnProperty.call(prodInventory, key)) {
            currentCart.Items.forEach((item: ShoppingCartItem) => {
              if (item.ProductCode === key) {
                item.InventoryLevel = prodInventory[key];
                item.needsAddress = currentState.needsAddress;

                if (item.InventoryLevel < item.Quantity) {
                  currentState.canCheckout = false;
                }
              }
            });
          }
        }
      });
    }

    this.setState({
      ...currentState,
      isLoading: false,
    });
  }

  async componentDidMount() {
    this.loadCart();
  }

  async handleQuantityClick(product: ShoppingCartItem, quantity: number) {
    if (product.Status === "Removed" && quantity === 1) {
      alert("undo not implemented yet!");
      //cartRepo.addItemToCart(product);
      this.loadCart(true);
      return;
    } else {
      try {
        const result = await cartRepo.adjustCartItem({
          ID: product.ID,
          ProductId: product.ProductId,
          Quantity: quantity,
        });

        if (result) {
          alert(result);
        }
      } catch (e: unknown) {
        let message = `Unable to adjust product levels.  Please try again.`;

        if (e instanceof Response) {
          message = await e.text();
        }
        if (e instanceof Error) {
          message = e.message;
        }
        alert(message);
      }
    }

    this.loadCart(true);
    return;
  }

  openLocationSelection = () => {
    openLocationSelection();
  };

  render() {
    if (this.state.isLoading) {
      return <CartLoadingUI></CartLoadingUI>;
    }

    if (!this.state.cart || this.state.cart.Items.length === 0) {
      return <EmptyCart />;
    }

    // inventory address requirements
    const requirement = locationRequirement(this.state.inventory);
    let locationSelectionVisible = false;

    if (!this.state.canCheckout && this.state.needsAddress) {
      locationSelectionVisible = true;
    }

    const marketplaceOnly: boolean =
      this.state.address.MarketplaceType === "loris";

    function handleLocationSelected(): void {
      safeReloadPage();
    }

    return (
      <>
        <CartContext.Provider
          value={{
            updateCartItem: this.handleQuantityClick.bind(this),
            selectAddress: this.openLocationSelection,
          }}
        >
          <div>
            <ShoppingCartWidget cart={this.state.cart} />

            <CheckoutButtons
              canCheckout={this.state.canCheckout}
              marketplaceAllowed={this.state.cart.MarketplaceAllowed}
              marketplaceOnly={marketplaceOnly}
            />

            <SimpleDialog
              open={locationSelectionVisible}
              title="Some items in your cart are only available at select locations."
            >
              <LocationSearchModal
                addressRequirement={requirement}
                onLocationSelected={handleLocationSelected}
                onZipSelected={handleLocationSelected}
              />
            </SimpleDialog>
          </div>
        </CartContext.Provider>
      </>
    );
  }
}
